import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Loader2, RotateCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  type GalleryUploadItem, type GalleryUploadSummary,
  cancelGalleryUploads, dismissGalleryUploads, removeGalleryUpload, retryGalleryUpload, summarize, useGalleryUploads,
} from './galleryUploadQueue'
import { FileThumbnail } from './FileThumbnail'

const SCROLLBAR = '[scrollbar-color:theme(colors.gray.300)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300'

export const ProgressBar = ({ percent, tone = 'primary', className }: { percent: number; tone?: 'primary' | 'success' | 'danger'; className?: string }) => (
  <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-gray-200', className)} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
    <div
      className={cn('h-full rounded-full transition-[width] duration-300', tone === 'success' ? 'bg-green-500' : tone === 'danger' ? 'bg-red-500' : 'bg-primary-500')}
      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
    />
  </div>
)

/** "12 de 40 enviadas · 34%", the failures and the bar for the whole batch. */
export const UploadOverall = ({ summary, className }: { summary: GalleryUploadSummary; className?: string }) => {
  const { t } = useTranslation('gallery')
  return (
    <div className={cn('space-y-1.5', className)} aria-live="polite">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span className="font-medium text-gray-900">
          {t('uploadQueue.overall', { sent: summary.sent, total: summary.total, percent: summary.percent })}
        </span>
        {summary.failed > 0 && <span className="text-red-600">{t('uploadQueue.failedCount', { count: summary.failed })}</span>}
      </div>
      <ProgressBar percent={summary.percent} tone={summary.finished ? (summary.failed > 0 ? 'danger' : 'success') : 'primary'} />
    </div>
  )
}

/**
 * The main button of a bulk upload, which doubles as its progress: "Enviando 12 de 40 · 34%"
 * with a primary fill that grows with the bytes sent, then "Processando N…", then "Concluído"
 * (clicking closes) or "N falharam · tentar de novo" (clicking retries the failures).
 * The label is drawn twice, the filled copy clipped to the progress, so it reads on both halves.
 */
export const UploadProgressButton = ({ summary, onDone, onBusyClick, className }: {
  summary: GalleryUploadSummary
  onDone: () => void
  /** While uploading or processing; without it the button only reports. */
  onBusyClick?: () => void
  className?: string
}) => {
  const { t } = useTranslation('gallery')
  const failedOnly = summary.finished && summary.failed > 0
  const fill = summary.finished || !summary.uploading ? 100 : summary.percent
  const label = summary.uploading
    ? t('uploadQueue.progressSending', { sent: summary.sent, total: summary.total, percent: summary.percent })
    : !summary.finished
      ? t('uploadQueue.progressProcessing', { count: summary.processing })
      : failedOnly
        ? t('uploadQueue.progressFailed', { count: summary.failed })
        : t('uploadQueue.progressDone')
  const icon = summary.finished
    ? (failedOnly ? <RotateCw className="h-4 w-4 flex-shrink-0" /> : <CheckCircle2 className="h-4 w-4 flex-shrink-0" />)
    : <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
  const content = (
    <span className="flex min-w-0 items-center justify-center gap-2 px-4">
      {icon}
      <span className="truncate tabular-nums">{label}</span>
    </span>
  )
  const busy = !summary.finished
  const handleClick = () => {
    if (failedOnly) retryGalleryUpload()
    else if (summary.finished) onDone()
    else onBusyClick?.()
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-busy={busy}
      aria-disabled={busy && !onBusyClick}
      className={cn(
        'relative inline-flex h-9 min-w-0 items-center justify-center overflow-hidden rounded-md border text-sm font-medium shadow-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        failedOnly ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100' : 'border-primary-200 bg-primary-50 text-primary-700',
        busy && !onBusyClick && 'cursor-default',
        className,
      )}
    >
      {content}
      {!failedOnly && (
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center bg-primary-600 text-primary-foreground transition-[clip-path] duration-300"
          style={{ clipPath: `inset(0 ${100 - Math.max(0, Math.min(100, fill))}% 0 0)` }}
        >
          {content}
        </span>
      )}
    </button>
  )
}

const UploadRow = ({ item }: { item: GalleryUploadItem }) => {
  const { t } = useTranslation('gallery')
  const reason = item.error?.kind === 'canceled'
    ? t('uploadQueue.canceledReason')
    : item.error?.kind === 'processing'
      ? t('uploadQueue.processingFailedReason')
      : item.error?.message || (item.error?.kind === 'rateLimited' ? t('uploadQueue.rateLimitedReason') : t('uploadQueue.genericReason'))
  const label = {
    waiting: t('uploadQueue.waiting'),
    optimizing: t('uploadQueue.optimizing'),
    sending: t('uploadQueue.sending', { percent: item.percent }),
    processing: t('uploadQueue.processing'),
    done: t('uploadQueue.done'),
    failed: t('uploadQueue.failedReason', { reason }),
  }[item.status]

  return (
    <li className="flex items-center gap-3 py-2">
      <div className="relative h-11 w-11 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
        <FileThumbnail file={item.file} alt="" className={cn(item.status === 'waiting' && 'opacity-60')} />
        {item.status === 'done' && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/30"><CheckCircle2 className="h-5 w-5 text-white" /></span>
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <p className="truncate text-sm font-medium text-gray-900" title={item.file.name}>{item.title || item.file.name}</p>
        {(item.status === 'sending' || item.status === 'processing') && <ProgressBar percent={item.percent} />}
        <p className={cn('flex items-center gap-1 text-xs', item.status === 'failed' ? 'text-red-600' : item.status === 'done' ? 'text-green-700' : 'text-gray-500')}>
          {(item.status === 'processing' || item.status === 'optimizing') && <Loader2 className="h-3 w-3 flex-shrink-0 animate-spin" />}
          {item.status === 'failed' && <AlertCircle className="h-3 w-3 flex-shrink-0" />}
          <span className="break-words">{label}</span>
        </p>
      </div>
      {item.status === 'failed' && (
        <div className="flex flex-shrink-0 items-center gap-1">
          <Button type="button" variant="outline" size="sm" className="h-8 px-2" aria-label={t('uploadQueue.retry')} onClick={() => retryGalleryUpload(item.id)}>
            <RotateCw className="h-3.5 w-3.5 sm:mr-1" />
            <span className="hidden sm:inline">{t('uploadQueue.retry')}</span>
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" aria-label={t('uploadQueue.remove')} onClick={() => removeGalleryUpload(item.id)}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
    </li>
  )
}

/** The per-file list; failures first so they are not lost below the fold. */
export const UploadList = ({ items, className }: { items: GalleryUploadItem[]; className?: string }) => {
  const ordered = [...items].sort((a, b) => Number(b.status === 'failed') - Number(a.status === 'failed'))
  return (
    <ul className={cn('divide-y divide-gray-100 overflow-y-auto', SCROLLBAR, className)}>
      {ordered.map((item) => <UploadRow key={item.id} item={item} />)}
    </ul>
  )
}

/**
 * Floating progress while the upload dialog is closed. Collapsed it is one line and a bar;
 * expanded it lists every file with retry. It goes away by itself after a clean finish.
 */
export const GalleryUploadPill = () => {
  const { t } = useTranslation('gallery')
  const { items, dialogOpen } = useGalleryUploads()
  const [expanded, setExpanded] = useState(false)
  const summary = summarize(items)
  const visible = items.length > 0 && !dialogOpen

  useEffect(() => {
    if (!visible || !summary.finished || summary.failed > 0) return
    const timer = window.setTimeout(dismissGalleryUploads, 6000)
    return () => window.clearTimeout(timer)
  }, [visible, summary.finished, summary.failed])

  if (!visible) return null

  return (
    <div className="fixed inset-x-4 bottom-4 z-30 sm:left-auto sm:right-20 sm:w-96" role="region" aria-label={t('uploadQueue.title')}>
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
        <div className="flex items-center gap-2 p-2">
          <UploadProgressButton
            summary={summary}
            onDone={dismissGalleryUploads}
            onBusyClick={() => setExpanded((open) => !open)}
            className="flex-1"
          />
          <Button
            type="button" variant="ghost" size="sm" className="h-8 w-8 flex-shrink-0 p-0"
            aria-expanded={expanded} aria-label={expanded ? t('uploadQueue.collapse') : t('uploadQueue.expand')}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
          {summary.finished && (
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 flex-shrink-0 p-0" aria-label={t('uploadQueue.close')} onClick={dismissGalleryUploads}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        {expanded && (
          <div className="space-y-2 border-t border-gray-100 px-4 pb-3 pt-3">
            <UploadOverall summary={summary} />
            <UploadList items={items} className="max-h-72" />
            {!summary.finished && (
              <div className="flex justify-end pt-1">
                <Button type="button" variant="outline" size="sm" onClick={cancelGalleryUploads}>{t('uploadQueue.cancelAll')}</Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
